import {
  listPreventiveCareAcknowledgementsResponseSchema,
  listPreventiveCareRecommendationsResponseSchema,
  preventiveCareAcknowledgementResponseSchema,
  type AcknowledgePreventiveCareRecommendationRequest,
  type PreventiveCareAcknowledgementResponse,
  type PreventiveCareRecommendationResponse,
} from '@emr/shared';
import { z } from 'zod';

import { apiRequest } from './client.ts';

/**
 * The preventive-care-gap DSI surface (`BACKLOG.md` C12): `GET .../preventive-care-recommendations`
 * (every currently-open gap, recomputed live), `GET .../preventive-care-recommendations/
 * acknowledgements` (the clinician-decision history, DSI source attributes snapshotted), and
 * `POST .../preventive-care-recommendations/:ruleId/acknowledge` (the one write this feature
 * performs — `server/src/clinical/preventiveCareRoutes.ts`'s own header). Same envelope-per-route-file
 * shape as `api/chart.ts`, kept in its own file rather than folded into that one since this surface
 * has its own route prefix and its own response envelopes, not a sibling of the flowsheet lists.
 */

const acknowledgementEnvelopeSchema = z.strictObject({
  acknowledgement: preventiveCareAcknowledgementResponseSchema,
});

export async function listPreventiveCareRecommendations(
  patientId: string,
  signal?: AbortSignal,
): Promise<readonly PreventiveCareRecommendationResponse[]> {
  const response = await apiRequest(
    `/api/patients/${patientId}/preventive-care-recommendations`,
    listPreventiveCareRecommendationsResponseSchema,
    signal === undefined ? {} : { signal },
  );
  return response.recommendations;
}

export async function listPreventiveCareAcknowledgements(
  patientId: string,
  signal?: AbortSignal,
): Promise<readonly PreventiveCareAcknowledgementResponse[]> {
  const response = await apiRequest(
    `/api/patients/${patientId}/preventive-care-recommendations/acknowledgements`,
    listPreventiveCareAcknowledgementsResponseSchema,
    signal === undefined ? {} : { signal },
  );
  return response.acknowledgements;
}

export async function acknowledgePreventiveCareRecommendation(
  patientId: string,
  ruleId: string,
  input: AcknowledgePreventiveCareRecommendationRequest,
): Promise<PreventiveCareAcknowledgementResponse> {
  const response = await apiRequest(
    `/api/patients/${patientId}/preventive-care-recommendations/${ruleId}/acknowledge`,
    acknowledgementEnvelopeSchema,
    { method: 'POST', body: input },
  );
  return response.acknowledgement;
}
